import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Camera, ArrowRight } from "lucide-react";
import locations, { categories } from "../data/locations";
import bwMap from "../data/bw-map.json";
import LazyVideo from "./LazyVideo";

/**
 * Drehorte-Karte Baden-Württemberg.
 * - SVG mit echtem BW-Outline (GeoJSON, vorab projiziert in /data/bw-map.json)
 * - Pins werden mit derselben equirectangular-Projektion wie der Outline gesetzt
 * - Hover (Desktop) / Tap (Mobile) → Detail-Card
 * - Pulse-Ring nur für Home-Base
 */
const LocationsMap = () => {
  const { viewBox, path, projection } = bwMap;
  const { minLon, maxLon, minLat, maxLat, lonScale, scaleX, pad } = projection;
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState(null);
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  const project = (lon, lat) => ({
    x: pad + (lon - minLon) * lonScale * scaleX,
    y: pad + (maxLat - lat) * scaleX,
  });

  const visibleLocations = useMemo(
    () => (filter ? locations.filter((l) => l.category === filter) : locations),
    [filter]
  );

  // Close tooltip on outside click (mobile)
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setActive(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const activePin = active && locations.find((l) => l.id === active);
  const activePos = activePin ? project(activePin.lon, activePin.lat) : null;

  return (
    <section
      id="drehorte"
      className="py-24 bg-secondary/30"
      data-testid="locations-section"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 text-[hsl(var(--accent))] text-xs font-semibold uppercase tracking-widest mb-4">
            <MapPin className="w-3.5 h-3.5" /> Drehorte
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="locations-title">
            Gedreht in ganz Baden-Württemberg
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Vom Schwarzwald über den Bodensee bis zur Schwäbischen Alb — {locations.length} Drehorte, eine konsistente Bildsprache.
          </p>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            onClick={() => setFilter(null)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              !filter
                ? "bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white"
                : "border-white/10 text-muted-foreground hover:bg-secondary/60 hover:text-white"
            }`}
            data-testid="filter-all"
          >
            Alle ({locations.length})
          </button>
          {Object.entries(categories).map(([key, meta]) => {
            const count = locations.filter((l) => l.category === key).length;
            if (count === 0) return null;
            const active = filter === key;
            return (
              <button
                key={key}
                onClick={() => setFilter(active ? null : key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors flex items-center gap-2 ${
                  active
                    ? "border-transparent text-white"
                    : "border-white/10 text-muted-foreground hover:bg-secondary/60 hover:text-white"
                }`}
                style={active ? { backgroundColor: meta.color } : undefined}
                data-testid={`filter-${key}`}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: meta.color }}
                />
                {meta.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Map + Sidebar grid */}
        <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-start">
          {/* SVG Map */}
          <div
            ref={containerRef}
            className="relative bg-background/40 border border-white/5 rounded-2xl overflow-hidden p-3 sm:p-4"
          >
            <svg
              viewBox={viewBox}
              className="block mx-auto"
              style={{
                width: "auto",
                height: "auto",
                maxWidth: "100%",
                maxHeight: "min(60vh, 520px)",
                aspectRatio: `${projection.width} / ${projection.height}`,
              }}
              role="img"
              aria-label="Karte Baden-Württemberg mit Drehorten"
            >
              {/* Outline glow */}
              <defs>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <radialGradient id="bwFill" cx="0.4" cy="0.5">
                  <stop offset="0%" stopColor="hsl(280, 75%, 18%)" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="hsl(280, 75%, 8%)" stopOpacity="0.2" />
                </radialGradient>
              </defs>

              <path
                d={path}
                fill="url(#bwFill)"
                stroke="hsl(280, 75%, 60%)"
                strokeWidth="1.5"
                strokeOpacity="0.6"
                vectorEffect="non-scaling-stroke"
              />

              {/* Pins */}
              {visibleLocations.map((loc) => {
                const { x, y } = project(loc.lon, loc.lat);
                const color = categories[loc.category]?.color || "#a855f7";
                const isActive = active === loc.id;
                const isHome = loc.home;
                return (
                  <g
                    key={loc.id}
                    transform={`translate(${x},${y})`}
                    className="cursor-pointer"
                    onMouseEnter={() => setActive(loc.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActive(isActive ? null : loc.id);
                    }}
                    onFocus={() => setActive(loc.id)}
                    tabIndex={0}
                    data-testid={`pin-${loc.id}`}
                  >
                    {/* Pulse ring (home only) */}
                    {isHome && (
                      <>
                        <circle
                          r="14"
                          fill="none"
                          stroke={color}
                          strokeWidth="2"
                          opacity="0.6"
                        >
                          <animate
                            attributeName="r"
                            values="8;28"
                            dur="2.4s"
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.7;0"
                            dur="2.4s"
                            repeatCount="indefinite"
                          />
                        </circle>
                      </>
                    )}
                    <circle
                      r={isActive ? 9 : isHome ? 8 : 6}
                      fill={color}
                      stroke="white"
                      strokeWidth="2"
                      style={{ transition: "r 0.15s ease" }}
                    />
                  </g>
                );
              })}
            </svg>

          </div>

          {/* Sidebar */}
          <div className="lg:sticky lg:top-24 space-y-4">
            {/* Active pin detail card */}
            {activePin && (
              <div className="bg-background/80 backdrop-blur-md border border-[hsl(var(--accent))]/30 rounded-xl overflow-hidden shadow-xl animate-in fade-in duration-200" data-testid="active-pin-card">
                {activePin.video && (
                  <div className={activePin.aspect === "9/16" ? "max-w-[60%] mx-auto" : ""}>
                    <LazyVideo
                      src={activePin.video}
                      poster={activePin.poster}
                      hoverToPlay={true}
                      aspectRatio={activePin.aspect || "16/9"}
                      className="bg-secondary"
                    />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: categories[activePin.category]?.color }}
                    />
                    <h3 className="font-bold truncate">{activePin.name}</h3>
                  </div>
                  <ul className="space-y-1 text-sm text-muted-foreground mb-3">
                    {activePin.projects.slice(0, 3).map((p, i) => (
                      <li key={i} className="flex gap-2">
                        <Camera className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-60" />
                        <span className="line-clamp-1">{p}</span>
                      </li>
                    ))}
                    {activePin.projects.length > 3 && (
                      <li className="text-xs opacity-70 pl-5">
                        +{activePin.projects.length - 3} weitere Projekte
                      </li>
                    )}
                  </ul>
                  <Link
                    to={`/drehort/${activePin.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--accent))] hover:underline"
                  >
                    Drehort-Details ansehen
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            )}

            <h3 className="text-sm uppercase tracking-widest text-muted-foreground">
              Drehorte ({visibleLocations.length})
            </h3>
            <ul className="space-y-2 max-h-[440px] overflow-y-auto pr-2">
              {visibleLocations.map((loc) => {
                const isActive = active === loc.id;
                const color = categories[loc.category]?.color;
                return (
                  <li key={loc.id}>
                    <Link
                      to={`/drehort/${loc.id}`}
                      onMouseEnter={() => setActive(loc.id)}
                      className={`block w-full text-left p-3 rounded-lg border transition-colors ${
                        isActive
                          ? "border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/10"
                          : "border-white/5 hover:bg-secondary/50"
                      }`}
                      data-testid={`list-${loc.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        <span className="font-medium text-sm">{loc.name}</span>
                        {loc.home && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]">
                            Base
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-2">
                        {loc.projects[0]}
                        {loc.projects.length > 1 && ` · +${loc.projects.length - 1} mehr`}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsMap;
